import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { FeedbackListItem, FeedbackStatus, FeedbackTypeValue } from '@property/shared';
import { useFeedbackList } from '@/hooks/use-feedback-queries';
import { useUpdateFeedbackStatus } from '@/hooks/use-feedback-mutations';
import { useUserProfile } from '@/hooks/use-task-queries';
import { DataTable, DataTableColumnHeader } from '@/components/data-table';
import { useTableRefresh } from '@/hooks/use-table-refresh';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { FeedbackDetailModal } from '@/components/feedback-detail-modal';
import { MessageSquare, AlertCircle } from 'lucide-react';
import { formatTimestamp } from '@/lib/date';

type FilterType = 'all' | FeedbackTypeValue;

const FILTER_OPTIONS: { label: string; value: FilterType }[] = [
  { label: 'All', value: 'all' },
  { label: 'General', value: 'general' },
  { label: 'Inspection', value: 'inspection_report' },
  { label: 'Contact Queries', value: 'contact_query' },
];

function MarkStatusButton({ item }: { item: FeedbackListItem }) {
  const { mutate, isPending } = useUpdateFeedbackStatus();
  const isUnread = item.status === FeedbackStatus.UNREAD;
  return (
    <Button
      size="sm"
      variant="default"
      disabled={isPending}
      className="w-28"
      onClick={(e) => {
        e.stopPropagation();
        mutate({ id: item.id, status: isUnread ? FeedbackStatus.READ : FeedbackStatus.UNREAD });
      }}
    >
      {isUnread ? 'Mark read' : 'Mark unread'}
    </Button>
  );
}

function FeedbackFromCell({ item }: { item: FeedbackListItem }) {
  const shouldFetch = !item.name && !!item.userId;
  const { data: userData } = useUserProfile(shouldFetch ? (item.userId ?? undefined) : undefined);
  const displayName = item.name ?? userData?.name ?? (item.userId ? 'Unknown' : '—');
  return (
    <div className="pl-6 space-y-0.5">
      <p className="text-sm font-medium truncate max-w-[160px]">{displayName}</p>
      {item.email && (
        <p className="text-xs text-muted-foreground truncate max-w-[160px]">{item.email}</p>
      )}
      {item.phone && <p className="text-xs text-muted-foreground">{item.phone}</p>}
    </div>
  );
}

const COLUMNS: ColumnDef<FeedbackListItem>[] = [
  {
    id: 'from',
    enableSorting: false,
    meta: { label: 'From', isPrimary: true, isMandatory: true },
    header: () => <span className="pl-6">From</span>,
    cell: ({ row }) => <FeedbackFromCell item={row.original} />,
  },
  {
    accessorKey: 'message',
    enableSorting: false,
    meta: { label: 'Message' },
    header: () => <span>Message</span>,
    cell: ({ row }) => {
      const { message } = row.original;
      return <p className="text-sm text-muted-foreground truncate max-w-xs">{message ?? '—'}</p>;
    },
  },
  {
    accessorKey: 'createdAt',
    enableSorting: true,
    meta: { label: 'Received' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Received" />,
    cell: ({ row }) => formatTimestamp(row.original.createdAt, { dateStyle: 'medium' }),
  },
  {
    id: 'actions',
    enableSorting: false,
    meta: { label: 'Action', isMandatory: true },
    header: () => <span>Action</span>,
    cell: ({ row }) => <MarkStatusButton item={row.original} />,
  },
];

export function FeedbackList() {
  const refresh = useTableRefresh(queryKeys.feedback.all());
  const [filter, setFilter] = useState<FilterType>('all');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>('desc');
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const typeFilter = filter === 'all' ? undefined : filter;
  const { data, total, isLoading, isFetching, isError } = useFeedbackList({
    viewPage: page,
    viewPageSize: 10,
    sortBy,
    sortOrder,
    type: typeFilter,
  });

  // Derive selected item from live data so modal reflects latest status after mutation
  const selectedItem =
    selectedItemId != null ? (data.find((i) => i.id === selectedItemId) ?? null) : null;

  function handleFilterChange(value: FilterType) {
    setFilter(value);
    setPage(1);
  }

  function handleRowClick(item: FeedbackListItem) {
    setSelectedItemId(item.id);
    setModalOpen(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {FILTER_OPTIONS.map(({ label, value }) => (
          <Button
            key={value}
            size="sm"
            variant={filter === value ? 'default' : 'outline'}
            onClick={() => handleFilterChange(value)}
          >
            {label}
          </Button>
        ))}
      </div>

      <DataTable
        tableId="feedback-list"
        showColumnToggle
        onRefresh={refresh}
        columns={COLUMNS}
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        pagination={{
          page,
          pageSize: 10,
          total,
          onPageChange: setPage,
        }}
        sorting={{
          sortBy,
          sortOrder,
          onSortChange: (by, order) => {
            setSortBy(by);
            setSortOrder(order);
            setPage(1);
          },
        }}
        onRowClick={handleRowClick}
        emptyState={
          isError
            ? {
                icon: <AlertCircle className="h-8 w-8 text-muted-foreground" />,
                title: 'Failed to load feedback',
                description: 'There was a problem fetching feedback. Please try again.',
              }
            : {
                icon: <MessageSquare className="h-8 w-8 text-muted-foreground" />,
                title: 'No feedback yet',
                description:
                  filter !== 'all'
                    ? 'Try a different filter.'
                    : 'Feedback will appear here once submitted.',
              }
        }
      />

      <FeedbackDetailModal item={selectedItem} open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
